import {
    faLock,
    faLockOpen,
    faRedo,
    faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import deepEqual from "fast-deep-equal";
import { isEqual } from "lodash-es";
import { ParsedUrlQuery } from "querystring";
import Random from "random-js";
import * as React from "react";
import { Breadcrumb, Button } from "react-bootstrap";
import uuidValidate from "uuid-validate";
import { useUser } from "../app/state";
import { Dictionary } from "../clay/common";
import { Editable } from "../clay/edit-context";
import { RecordMeta } from "../clay/meta";
import {
    BaseAction,
    Page,
    PageContext,
    PageRequest,
    ReduceResult,
    usePageContext,
} from "../clay/Page";
import {
    DeleteRecordResult,
    Record,
    StoreRecordResult,
} from "../clay/server/api";
import { Widget, WidgetRequest } from "../clay/widgets";
import { hasPermission } from "../permissions";
import { fetchRecord, storeRecord, useRequest } from "./api";
import { EditorsContext } from "./editors";
import { QuickCacheApi, useQuickCache } from "./quick-cache";
import {
    castRequest,
    DeleteRequest,
    EditingRecordRequest,
    FinishedRequest,
    LocalStoreRequest,
    PrintRequest,
    Request,
    RequestHandle,
    StoreRequest,
} from "./requests";
import { titleCase } from "./title-case";

function isMoreChange(oldAction: any, newAction: any): boolean {
    if (!oldAction || !newAction) {
        return false;
    }
    if (Object.keys(oldAction).length !== Object.keys(newAction).length) {
        return false;
    }
    for (const key in oldAction) {
        if (key === "value" && oldAction.type === "SET") {
            // ok
        } else if (key != "action") {
            if (!deepEqual(oldAction[key], newAction[key])) {
                return false;
            }
        } else {
            if (!isMoreChange(oldAction.action, newAction.action)) {
                return false;
            }
        }
    }
    return true;
}

function isBlur(action: any): boolean {
    if ("action" in action) {
        return isBlur(action.action);
    }
    return action.type === "BLUR";
}

function isBoring(oldAction: any, newAction: any): boolean {
    return isMoreChange(oldAction, newAction) || isBlur(newAction);
}

type State<StateType, DataType, ActionType> = {
    currentRecordId: string | null;
    currentRecord: null | DataType;
    saved: null | DataType;
    loaded: null | DataType;
    state:
        | "editing"
        | "saving"
        | "saved"
        | "deleting"
        | "deleted"
        | "printing"
        | "printed"
        | "duplicating"
        | "duplicated";
    ui: StateType | null;
    sentRequests: Dictionary<WidgetRequest>;
    resolvedValues: Dictionary<WidgetRequest>;
    scheduledPrint: {
        template: string;
        parameters: string[];
    } | null;
    tail: string[];
    category: string | null;
    lock: "nolock" | "locked" | "unlocked";
    watching: boolean;
    editors: {
        id: string;
        username: string;
        timestamp: Date;
    }[];
    parameters: ParsedUrlQuery;
    undos: { data: DataType; state: StateType }[];
    redos: { data: DataType; state: StateType }[];
    lastUndoAction: ActionType | null;
};
export type TableWidgetPageState<StateType, DataType, ActionType> = State<
    StateType,
    DataType,
    ActionType
>;

export type Action<ActionType, DataType> =
    | {
          type: "LOAD";
          record: DataType | null;
      }
    | {
          type: "RECORD";
          action: ActionType;
      }
    | {
          type: "SAVE";
          printTemplate: string | null;
          templateParameters: string[];
      }
    | {
          type: "PRINT";
          printTemplate: string;
          templateParameters: string[];
      }
    | {
          type: "SAVED";
          response: StoreRecordResult;
      }
    | {
          type: "DUPLICATED";
          response: StoreRecordResult;
      }
    | {
          type: "DUPLICATE";
      }
    | {
          type: "DELETE";
      }
    | {
          type: "DELETED";
          response: DeleteRecordResult;
      }
    | {
          type: "AUTOSAVED";
          response: StoreRecordResult;
      }
    | {
          type: "RESOLVE_REQUEST";
          request: WidgetRequest;
          key: string;
          response: any;
      }
    | {
          type: "TOGGLE_LOCK";
      }
    | {
          type: "SUBSTITUTE";
          key: string;
      }
    | {
          type: "EDITING_UPDATED";
          editors: {
              userId: string;
              username: string;
              timestamp: string;
          }[];
      }
    | {
          type: "UNDO";
      }
    | {
          type: "REDO";
      }
    | BaseAction;

export type TableWidgetPageAction<ActionType, DataType> = Action<
    ActionType,
    DataType
>;

type Props<StateType, DataType, ActionType> = {
    state: State<StateType, DataType, ActionType>;
    dispatch: (action: Action<ActionType, DataType>) => void;
};

export type TableWidgetOptions<
    StateType,
    DataType,
    ContextType,
    ActionType,
    JsonType,
    BrokenJsonType
> = {
    meta: Widget<StateType, DataType, ContextType, ActionType, {}> & {
        dataMeta: RecordMeta<DataType, JsonType, BrokenJsonType>;
    };
    makeContext: (context: PageContext) => ContextType;
    applyName?: (record: DataType, name: string) => DataType;
    applyCategory?: (record: DataType, name: string) => DataType;
    updater?: (record: DataType, requests: Dictionary<any>) => DataType;
    locked?: (record: DataType) => boolean;
    requests?: (
        state: StateType,
        record: DataType
    ) => Dictionary<WidgetRequest>;
    initialize?: (record: DataType, context: ContextType) => DataType;
    autoSave?: boolean;
    title: (record: DataType, cache: QuickCacheApi) => string;
    name?: string;
    postSave?: (record: DataType) => void;
    disableFinish?: boolean;
};

export function TableWidgetPage<
    StateType,
    DataType,
    ContextType,
    ActionType,
    ThisRequestType extends PageRequest,
    JsonType,
    BrokenJsonType
>(
    options: TableWidgetOptions<
        StateType,
        DataType,
        ContextType,
        ActionType,
        JsonType,
        BrokenJsonType
    >
): Page<State<StateType, DataType, ActionType>, Action<ActionType, DataType>> {
    const meta = options.meta;

    const updater = options.updater || ((data, requests) => data);
    const locked = options.locked || ((data) => false);

    const table = options.meta.dataMeta.name;

    const name = options.name || titleCase(table);

    function initialize(
        segments: string[],
        parameters: ParsedUrlQuery,
        context: PageContext
    ): ReduceResult<
        State<StateType, DataType, ActionType>,
        Action<ActionType, DataType>
    > {
        const { state, requests } = reduce(
            {
                scheduledPrint: null,
                currentRecordId: null,
                currentRecord: null,
                saved: null,
                loaded: null,
                state: "editing",
                ui: null,
                sentRequests: {},
                resolvedValues: {},
                tail: [],
                category: null,
                lock: "nolock",
                watching: false,
                editors: [],
                parameters,
                undos: [],
                redos: [],
                lastUndoAction: null,
            },
            {
                type: "UPDATE_PARAMETERS",
                segments,
                parameters,
            },
            context
        );

        return {
            state,
            requests,
        };
    }

    function component(props: Props<StateType, DataType, ActionType>) {
        const user = useUser();
        const context = usePageContext();
        const record = useRequest(async () => {
            if (props.state.currentRecordId === "new") {
                let record = options.meta.dataMeta.fromJSON(
                    options.meta.dataMeta.repair(undefined, new Random())
                );
                if (options.initialize) {
                    record = options.initialize(
                        record,
                        options.makeContext(context)
                    );
                }
                if (options.applyName && props.state.parameters.name) {
                    record = options.applyName(
                        record,
                        decodeURIComponent(
                            props.state.parameters.name as string
                        )
                    );
                }
                if (options.applyCategory) {
                    let category = props.state.parameters.category as string;
                    record = options.applyCategory(
                        record,
                        decodeURIComponent(category)
                    );
                }

                props.dispatch({
                    type: "LOAD",
                    record: record,
                });
            } else {
                props.dispatch({
                    type: "LOAD",
                    record: await fetchRecord(
                        options.meta.dataMeta,
                        props.state.currentRecordId || ""
                    ),
                });
            }
        }, [options.meta.dataMeta, props.state.currentRecordId]);

        const cache = useQuickCache();

        if (!hasPermission(user, options.meta.dataMeta.name, "read")) {
            throw new Error("User does not have permission");
        }

        const subdispatch = React.useCallback(
            (action: ActionType) => props.dispatch({ type: "RECORD", action }),
            [props.dispatch]
        );

        if (!props.state.ui || !props.state.currentRecord) {
            return <div />;
        }

        const validation = meta.validate(props.state.currentRecord, cache);

        const isLocked =
            props.state.lock === "locked" ||
            !hasPermission(user, options.meta.dataMeta.name, "write");

        const isBusy =
            props.state.state == "saving" ||
            props.state.state == "deleting" ||
            props.state.state == "printing" ||
            props.state.state == "duplicating";

        return (
            <div
                style={{
                    margin: "10px",
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    flexGrow: 1,
                }}
            >
                <Editable
                    save={
                        hasPermission(user, options.meta.dataMeta.name, "write")
                            ? {
                                  onClick: (template, parameters) =>
                                      props.dispatch({
                                          type: "SAVE",
                                          printTemplate: template,
                                          templateParameters: parameters || [],
                                      }),
                                  disabled:
                                      isBusy ||
                                      validation.length > 0 ||
                                      isLocked,
                                  active: props.state.state === "saving",
                                  completed: props.state.state === "saved",
                              }
                            : undefined
                    }
                    print={
                        hasPermission(user, options.meta.dataMeta.name, "write")
                            ? {
                                  onClick: (template, parameters) =>
                                      props.dispatch({
                                          type: "PRINT",
                                          printTemplate: template,
                                          templateParameters: parameters || [],
                                      }),
                                  disabled:
                                      isBusy ||
                                      (!isLocked && validation.length > 0),
                                  active: props.state.state === "printing",
                                  completed: props.state.state === "printed",
                              }
                            : undefined
                    }
                    delete={
                        hasPermission(
                            user,
                            options.meta.dataMeta.name,
                            "delete"
                        ) && props.state.currentRecordId !== "new"
                            ? {
                                  onClick: () => {
                                      if (
                                          confirm(
                                              "Are you sure you want to delete?"
                                          )
                                      ) {
                                          props.dispatch({
                                              type: "DELETE",
                                          });
                                      }
                                  },
                                  disabled: isBusy || isLocked,
                                  active: props.state.state === "deleting",
                                  completed: props.state.state === "deleted",
                              }
                            : undefined
                    }
                    duplicate={
                        hasPermission(user, options.meta.dataMeta.name, "new")
                            ? {
                                  onClick: () => {
                                      props.dispatch({ type: "DUPLICATE" });
                                  },
                                  disabled: isBusy || validation.length > 0,
                                  active: props.state.state === "duplicating",
                                  completed: props.state.state === "duplicated",
                              }
                            : undefined
                    }
                    substitute={
                        props.state.watching
                            ? {
                                  onClick: (key) => {
                                      props.dispatch({
                                          type: "SUBSTITUTE",
                                          key,
                                      });
                                  },
                                  disabled: false,
                                  active: false,
                                  completed: false,
                              }
                            : undefined
                    }
                >
                    <EditorsContext.Provider value={props.state.editors}>
                        <meta.component
                            data={props.state.currentRecord}
                            state={props.state.ui}
                            dispatch={subdispatch}
                            status={{
                                mutable: !isBusy && !isLocked,
                                validation,
                            }}
                        />
                    </EditorsContext.Provider>
                </Editable>
            </div>
        );
    }

    function reduce(
        state: State<StateType, DataType, ActionType>,
        action: Action<ActionType, DataType>,
        context: PageContext
    ): ReduceResult<
        State<StateType, DataType, ActionType>,
        Action<ActionType, DataType>
    > {
        let inner = baseReduce(state, action, context);

        if (inner.state.currentRecord && inner.state.ui) {
            const newData = updater(
                inner.state.currentRecord,
                inner.state.resolvedValues
            );

            const newRequests = [...inner.requests];
            let requests: Dictionary<WidgetRequest> = {};
            let values: Dictionary<any> = {};

            const innerReq = {
                ...(options.requests
                    ? options.requests(
                          inner.state.ui,
                          inner.state.currentRecord
                      )
                    : {}),
            };

            Object.entries(innerReq).forEach(([requestKey, requestValue]) => {
                if (
                    !isEqual(requestValue, inner.state.sentRequests[requestKey])
                ) {
                    newRequests.push({
                        type: requestValue.type,
                        request: requestValue.request,
                        decorator: (response: any) => ({
                            type: "RESOLVE_REQUEST",
                            key: requestKey,
                            request: requestValue,
                            response,
                        }),
                    });
                    values[requestKey] = null;
                } else {
                    values[requestKey] = inner.state.resolvedValues[requestKey];
                }

                requests[requestKey] = requestValue;
            });

            return {
                state: {
                    ...inner.state,
                    sentRequests: requests,
                    resolvedValues: values,
                    currentRecord: newData,
                },
                requests: newRequests,
            };
        } else {
            return inner;
        }
    }

    function beforeUnload(state: State<StateType, DataType, ActionType>) {
        if (options.autoSave && state.currentRecord) {
            storeRecord(options.meta.dataMeta, name, state.currentRecord);
            return true;
        } else {
            return false;
        }
    }

    function baseReduce(
        state: State<StateType, DataType, ActionType>,
        action: Action<ActionType, DataType>,
        context: PageContext
    ): ReduceResult<
        State<StateType, DataType, ActionType>,
        Action<ActionType, DataType>
    > {
        switch (action.type) {
            case "LOAD": {
                let record: DataType;
                if (action.record === null) {
                    record = options.meta.dataMeta.fromJSON(
                        options.meta.dataMeta.repair(
                            {
                                id: state.currentRecordId,
                                recordVersion: null,
                            } as any,
                            context.random
                        )
                    );
                } else {
                    record = action.record;
                }

                const inner = meta.initialize(
                    options.initialize
                        ? options.initialize(
                              record,
                              options.makeContext(context)
                          )
                        : record,
                    options.makeContext(context),
                    state.tail
                );
                return {
                    state: {
                        ...state,
                        currentRecord: inner.data,
                        saved: null,
                        loaded: record,
                        state: "editing",
                        ui: inner.state,
                        lock: locked(record) ? "locked" : "nolock",
                    },

                    requests:
                        state.currentRecordId !== "new"
                            ? [
                                  castRequest(
                                      Request<
                                          EditingRecordRequest,
                                          Action<ActionType, DataType>
                                      >(
                                          "EDIT",
                                          {
                                              tableName: table,
                                              id: state.currentRecordId as string,
                                          },
                                          (response) => ({
                                              type: "EDITING_UPDATED",
                                              editors: response.editors,
                                          })
                                      )
                                  ),
                              ]
                            : [],
                };
            }
            case "UPDATE_PARAMETERS": {
                const recordKeyId = action.segments[0];
                let tail = action.segments.slice(1);
                if (
                    !recordKeyId.startsWith("new") &&
                    !uuidValidate(recordKeyId)
                ) {
                    throw new Error("invalid path");
                }

                return {
                    state: {
                        ...state,
                        currentRecordId: recordKeyId,
                        currentRecord: null,
                        state: "editing",
                        ui: null,
                        tail,
                        category: null,
                        parameters: action.parameters,
                        watching: action.parameters.watching !== undefined,
                    },
                    requests: [],
                };
            }
            case "RESOLVE_REQUEST": {
                if (
                    isEqual(action.request, state.sentRequests[action.key]) &&
                    state.ui &&
                    state.currentRecord
                ) {
                    const resolvedValues = {
                        ...state.resolvedValues,
                        [action.key]: action.response,
                    };

                    return {
                        state: {
                            ...state,
                            resolvedValues,
                        },
                        requests: [],
                    };
                } else {
                    return { state, requests: [] };
                }
            }
            case "PAGE_ACTIVATED": {
                return { state, requests: [] };
            }
            case "HEARTBEAT": {
                return {
                    state,
                    requests:
                        state.currentRecordId && state.currentRecordId !== "new"
                            ? [
                                  castRequest(
                                      Request<
                                          EditingRecordRequest,
                                          Action<ActionType, DataType>
                                      >(
                                          "EDIT",
                                          {
                                              tableName: table,
                                              id: state.currentRecordId,
                                          },
                                          (response) => ({
                                              type: "EDITING_UPDATED",
                                              editors: response.editors,
                                          })
                                      )
                                  ),
                              ]
                            : [],
                };
            }
            case "EDITING_UPDATED": {
                return {
                    state: {
                        ...state,
                        editors: action.editors.map((row) => ({
                            id: row.userId,
                            username: row.username,
                            timestamp: new Date(row.timestamp),
                        })),
                    },
                    requests: [],
                };
            }
            case "DUPLICATE":
                if (!state.currentRecord) {
                    throw new Error();
                }
                const newRecord = {
                    ...state.currentRecord,
                    id: { uuid: context.random.uuid4() },
                };
                return {
                    state: {
                        ...state,
                        currentRecordId: "new",
                        currentRecord: newRecord,
                        state: "duplicating",
                    },
                    requests: [
                        castRequest(
                            Request<StoreRequest, Action<ActionType, DataType>>(
                                "STORE",
                                {
                                    tableName: table,
                                    form: name,
                                    record: options.meta.dataMeta.toJSON(
                                        newRecord
                                    ),
                                },
                                (response) => ({
                                    type: "DUPLICATED",
                                    response,
                                })
                            )
                        ),
                    ],
                };
            case "SAVE": {
                if (state.currentRecord === null) {
                    throw new Error("not expected");
                }
                return {
                    state: {
                        ...state,
                        state: "saving",
                        scheduledPrint: action.printTemplate
                            ? {
                                  template: action.printTemplate,
                                  parameters: action.templateParameters,
                              }
                            : null,
                    },
                    requests: [
                        castRequest(
                            Request<StoreRequest, Action<ActionType, DataType>>(
                                "STORE",
                                {
                                    tableName: table,
                                    form: name,
                                    record: options.meta.dataMeta.toJSON(
                                        state.currentRecord
                                    ) as any as Record,
                                },
                                (response) => ({
                                    type: "SAVED",
                                    response,
                                })
                            )
                        ),
                    ],
                };
            }
            case "PRINT": {
                const requests = [];

                requests.push(
                    castRequest(
                        Request<PrintRequest, Action<ActionType, DataType>>(
                            "PRINT",
                            {
                                template: action.printTemplate,
                                id: state.currentRecordId as string,
                                parameters: action.templateParameters,
                            },
                            () => {
                                throw new Error();
                            }
                        )
                    ) as any
                );

                if (!options.disableFinish) {
                    requests.push(
                        castRequest(
                            Request<
                                FinishedRequest,
                                Action<ActionType, DataType>
                            >("FINISHED", state.currentRecordId, () => {
                                throw new Error();
                            })
                        )
                    );
                }

                return {
                    state,
                    requests,
                };
            }
            case "SUBSTITUTE": {
                if (options.disableFinish) {
                    throw new Error("Unworkable");
                }
                return {
                    state,
                    requests: [
                        castRequest(
                            Request<
                                FinishedRequest,
                                Action<ActionType, DataType>
                            >("FINISHED", action.key, () => {
                                throw new Error();
                            })
                        ),
                    ],
                };
            }
            case "AUTOSAVED":
            case "SAVED":
            case "DUPLICATED": {
                const response = action.response;

                const record = options.meta.dataMeta.fromJSON(
                    response.record as any as JsonType
                );

                if (options.postSave) {
                    options.postSave(record);
                }

                const requests = [];

                if (state.scheduledPrint) {
                    requests.push(
                        castRequest(
                            Request<PrintRequest, Action<ActionType, DataType>>(
                                "PRINT",
                                {
                                    template: state.scheduledPrint.template,
                                    id: response.record.id,
                                    parameters: state.scheduledPrint.parameters,
                                },
                                () => {
                                    throw new Error();
                                }
                            )
                        ) as any
                    );
                }

                if (action.type === "SAVED" && !options.disableFinish) {
                    requests.push(
                        castRequest(
                            Request<
                                FinishedRequest,
                                Action<ActionType, DataType>
                            >("FINISHED", response.record.id, () => {
                                throw new Error();
                            })
                        )
                    );
                }

                return {
                    state: {
                        ...state,
                        state:
                            action.type === "DUPLICATED"
                                ? "duplicated"
                                : "saved",
                        currentRecordId: response.record.id,
                        currentRecord: record,
                        saved: record,
                        loaded: record,
                    },
                    requests,
                };
            }
            case "DELETE": {
                if (state.currentRecord === null) {
                    throw new Error("not expected");
                }
                return {
                    state: {
                        ...state,
                        state: "deleting",
                    },
                    requests: [
                        castRequest(
                            Request<
                                DeleteRequest,
                                Action<ActionType, DataType>
                            >(
                                "DELETE",
                                {
                                    tableName: table,
                                    form: name,
                                    recordId: state.currentRecordId as string,
                                },
                                (response) => ({
                                    type: "DELETED",
                                    response,
                                })
                            )
                        ),
                    ],
                };
            }
            case "DELETED": {
                const response = action.response;

                const requests: RequestHandle<
                    PageRequest,
                    Action<ActionType, DataType>
                >[] = [];
                requests.push(
                    castRequest(
                        Request<FinishedRequest, Action<ActionType, DataType>>(
                            "FINISHED",
                            "",
                            () => {
                                throw new Error();
                            }
                        )
                    )
                );

                return {
                    state: {
                        ...state,
                        state: "deleted",
                    },
                    requests,
                };
            }
            case "TOGGLE_LOCK":
                return {
                    state: {
                        ...state,
                        lock: state.lock === "locked" ? "unlocked" : "locked",
                    },
                    requests: [],
                };
            case "UNDO": {
                const undos = state.undos.slice();
                const undo = undos.pop()!;
                const redos = state.redos.slice();
                redos.push({
                    data: state.currentRecord!,
                    state: state.ui!,
                });
                return {
                    state: {
                        ...state,
                        currentRecord: undo.data,
                        ui: undo.state,
                        undos,
                        redos,
                    },
                    requests: [],
                };
            }
            case "REDO": {
                const undos = state.undos.slice();
                const redos = state.redos.slice();
                const redo = redos.pop()!;
                undos.push({
                    data: state.currentRecord!,
                    state: state.ui!,
                });
                return {
                    state: {
                        ...state,
                        currentRecord: redo.data,
                        ui: redo.state,
                        undos,
                        redos,
                    },
                    requests: [],
                };
            }

            case "RECORD": {
                if (state.currentRecord == null) {
                    throw new Error("missing company");
                }
                if (state.ui == null) {
                    throw new Error("missing company");
                }
                const inner = meta.reduce(
                    state.ui,
                    state.currentRecord,
                    action.action,
                    options.makeContext(context)
                );
                const boring = isBoring(state.lastUndoAction, action.action);

                let undos = [];
                if (boring) {
                    undos = state.undos;
                } else {
                    undos = state.undos.slice(
                        state.undos.length > 1000 ? 1 : 0
                    );
                    undos.push({ data: state.currentRecord, state: state.ui });
                }

                return {
                    state: {
                        ...state,
                        state: "editing",
                        currentRecord: inner.data,
                        ui: inner.state,
                        undos,
                        redos: [],
                        lastUndoAction: boring
                            ? state.lastUndoAction
                            : action.action,
                    },
                    requests: options.autoSave
                        ? [
                              castRequest(
                                  Request<
                                      LocalStoreRequest,
                                      Action<ActionType, DataType>
                                  >(
                                      "LOCAL_STORE",
                                      {
                                          tableName: table,
                                          form: name,
                                          record: options.meta.dataMeta.toJSON(
                                              inner.data
                                          ) as any as Record,
                                      },
                                      () => {
                                          throw new Error();
                                      }
                                  )
                              ),
                          ]
                        : [],
                };
            }
        }
    }

    function encodeState(state: State<StateType, DataType, ActionType>): {
        segments: string[];
        parameters: Dictionary<string>;
    } {
        return state.currentRecordId === null
            ? {
                  segments: [],
                  parameters: {},
              }
            : {
                  segments: [
                      state.currentRecordId,
                      ...(meta.encodeState && state.ui
                          ? meta.encodeState(state.ui)
                          : []),
                  ],
                  parameters:
                      state.currentRecordId === "new" && state.category !== null
                          ? { category: state.category }
                          : {},
              };
    }

    return {
        initialize,
        component,
        reduce,
        encodeState,
        title(state, cache) {
            if (state.currentRecord) {
                return options.title(state.currentRecord, cache);
            } else {
                return "Loading...";
            }
        },
        hasUnsavedChanges(state) {
            return (
                !options.autoSave &&
                !isEqual(state.currentRecord, state.loaded) &&
                state.state !== "deleted"
            );
        },
        beforeUnload,
        headerComponent(props) {
            const user = useUser();
            const cache = useQuickCache();

            const onUndo = React.useCallback(
                () => props.dispatch({ type: "UNDO" }),
                [props.dispatch]
            );
            const onRedo = React.useCallback(
                () => props.dispatch({ type: "REDO" }),
                [props.dispatch]
            );

            let title = "Loading...";
            if (props.state.currentRecord) {
                title = options.title(props.state.currentRecord, cache);
            }

            let lock = <div />;
            if (props.state.lock != "nolock") {
                lock = (
                    <div
                        style={{ padding: "5px", display: "inline-block" }}
                        onClick={() => {
                            hasPermission(user, table, "unlock") &&
                                props.dispatch({
                                    type: "TOGGLE_LOCK",
                                });
                        }}
                    >
                        <FontAwesomeIcon
                            icon={
                                props.state.lock == "locked"
                                    ? faLock
                                    : faLockOpen
                            }
                        />
                    </div>
                );
            }
            return (
                <>
                    <Breadcrumb>
                        <Breadcrumb.Item active>
                            {lock}
                            {title}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                    <div style={{ flexGrow: 1 }} />
                    <Button
                        disabled={props.state.undos.length == 0}
                        onClick={onUndo}
                    >
                        <FontAwesomeIcon icon={faUndo} />
                    </Button>
                    <Button
                        disabled={props.state.redos.length === 0}
                        onClick={onRedo}
                    >
                        <FontAwesomeIcon icon={faRedo} />
                    </Button>
                </>
            );
        },
    };
}
